// In the native app a tapped file link loads inside the webview, which shows
// a CSV as raw text and a PDF with no way back. Files are fetched here and
// handed to a native save bridge instead: downloadFile (any file, exact
// bytes) where the app lists it in window.appBridgeEvents, else the older
// text-only downloadCsv for CSVs. On older apps a PDF keeps the link, and a
// file whose name doesn't say is fetched and decided by its served type.

const FILE_DOWNLOAD_EVENT = 'downloadFile';
const EXTENSION_BY_CONTENT_TYPE = { 'application/pdf': 'pdf', 'text/csv': 'csv' };
const BASE64_CHUNK_BYTES = 0x8000;

function _hasPdfName(block) {
  return typeof block.name === 'string' && block.name.toLowerCase().endsWith('.pdf');
}

function _servedContentType(response) {
  return (response.headers.get('content-type') || '').split(';')[0].trim().toLowerCase();
}

// 'file' | 'csv' | null: which native bridge saves this block, if any.
// 'csv' means "try the CSV bridge": fetchCsvForNativeSave confirms it.
export function nativeSaveMethod(block, bridgeEvents) {
  if (!block || block.type !== 'file') { return null; }
  if (Array.isArray(bridgeEvents) && bridgeEvents.includes(FILE_DOWNLOAD_EVENT)) { return 'file'; }
  return _hasPdfName(block) ? null : 'csv';
}

export function fileNameWithExtension(name, contentType) {
  const base = typeof name === 'string' && name ? name : 'file';
  const ext = EXTENSION_BY_CONTENT_TYPE[contentType];
  if (!ext || base.toLowerCase().endsWith(`.${ext}`)) { return base; }
  return `${base}.${ext}`;
}

export function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += BASE64_CHUNK_BYTES) {
    binary += String.fromCharCode(...bytes.subarray(i, i + BASE64_CHUNK_BYTES));
  }
  return btoa(binary);
}

async function _fetchChatFile(block, fetchFn) {
  const response = await fetchFn(block.url, { credentials: 'same-origin' });
  if (!response.ok) {
    throw new Error(`Chat file request failed with status ${response.status}`);
  }
  return response;
}

export async function fetchFileForNativeSave(block, fetchFn) {
  const response = await _fetchChatFile(block, fetchFn);
  return {
    base64: arrayBufferToBase64(await response.arrayBuffer()),
    fileName: fileNameWithExtension(block.name, _servedContentType(response)),
  };
}

// null when the server says it isn't a CSV; the caller opens the link then.
export async function fetchCsvForNativeSave(block, fetchFn) {
  const response = await _fetchChatFile(block, fetchFn);
  if (_servedContentType(response) !== 'text/csv') { return null; }
  return { content: await response.text(), fileName: fileNameWithExtension(block.name, 'text/csv') };
}
